import React from 'react';
import { useParams } from 'react-router-dom';
import { getPrice, translate } from '../utils/translator';
import { useCartContext } from '../customHooks/useCart';
import { clientConfig } from '../config';
import videoConsultationDoctorImage from '../icons/start-consultation/video/video_consultation_doctor.png';
import { formatDateStamp, formatTimestamp } from '../utils/timestampFormatter';
import { TIMEZONES } from '../utils/consts';

interface VideoConsultationRowProps {
  className?: string;
  showTitle?: boolean;
  showPrice?: boolean;
}

const VideoConsultationRow: React.FC<VideoConsultationRowProps> = ({
  className = '',
  showTitle = true,
  showPrice = true
}) => {
  const { language } = useParams();
  const { getCart, cartCurrency } = useCartContext();
  const cart = getCart();
  // Don't render if no video consultation or booking data
  if (cart.consultation_type !== "video" || !cart.timekitBooking) {
    return null;
  }

  // Format date as DD.MM.YYYY in Germany timezone
  const formatDate = (start: string) => {
    return formatDateStamp(start, {
      useNumericFormat: true,
      showYear: true,
      locale: cart.locale_id,
      timeZone: TIMEZONES[cart.locale_id?.split('-')[1] as keyof typeof TIMEZONES || 'DE']
    });
  };

  // Format time as HH:mm in Germany timezone
  const formatTime = (start: string) => {
    return formatTimestamp(start, {
      showDate: false,
      showTime: true,
      use24Hour: true,
      useNumericTimeFormat: true,
      locale: cart.locale_id,
      timeZone: TIMEZONES[cart.locale_id?.split('-')[1] as keyof typeof TIMEZONES || 'DE']
    });
  };

  // Determine doctor image - use video consultation doctor image if no specific doctor was selected (preferred time booking)
  const timekitBooking = cart.timekitBooking as any;
  const isPreferredTimeBooking = !timekitBooking.doctorImagePath || !timekitBooking.doctorName;

  // Format time range for preferred time bookings (start - end)
  const formatTimeRange = (): string => {
    if (!isPreferredTimeBooking || !timekitBooking.end) {
      return formatTime(timekitBooking.start);
    }
    return `${formatTime(timekitBooking.start)}-${formatTime(timekitBooking.end)}`;
  };
  
  const doctorImageSrc = isPreferredTimeBooking 
    ? videoConsultationDoctorImage 
    : clientConfig.IMAGES_URL + timekitBooking.doctorImagePath;

  return (
    <div
      id="video-consultation-row"
      className={`flex justify-between items-start ${className}`}
    >
      <div id="video-consultation-row-details" className="flex-1">
        {showTitle && (
          <h4
            id="video-consultation-row-title"
            className="font-roboto text-base font-bold text-[#0D2C54] mb-1"
          >
            {translate(language, "default", cart.is_follow_up_consultation ? "OrderWindow/consultation_fee_follow_up" : "OrderWindow/consultation_fee")}
          </h4>
        )}
        <div className="flex flex-row justify-between pt-3 pb-4 pr-3">
          <div id="video-consultation-row-info" className="flex items-center gap-2 ml-2">
            <div style={{ backgroundColor: '#F5F7F5', borderRadius: '8px' }}>
              <img
                src={doctorImageSrc}
                alt="Doctor"
                width={60}
                height={60}
                style={{ borderRadius: '8px', display: 'block' }}
              />
            </div>
            <div className="flex flex-col w-full">
              <div id="video-consultation-row-doctor" className="flex mb-1">
                <span
                  id="video-consultation-row-doctor-name"
                  className="text-[#0D2C54] text-[12px] font-medium"
                >
                  {(cart.timekitBooking as any).doctorName || translate(language, 'choose_doctor', 'preferred_time/video_consultation_doctor')}
                </span>
              </div>
              <div id="video-consultation-row-date" className="flex flex-col">
                <span
                  id="video-consultation-row-date-value"
                  className="text-[#9D9D9D] text-[12px]"
                >
                  {translate(language, "default", "OrderWindow/consultation_date")}: {formatDate(cart.timekitBooking.start)}
                </span>
                <span
                  id="video-consultation-row-time-value"
                  className="text-[#9D9D9D] text-[12px]"
                >
                  {translate(language, "default", "OrderWindow/consultation_time")}: {formatTimeRange()}
                </span>
              </div>
            </div>
          </div>
          {showPrice && (
            <div id="video-consultation-row-price-container" className="">
              <span
                id="video-consultation-row-price"
                className="font-roboto text-base font-medium text-[#0D2C54]"
              >
                {getPrice(cart.consultation_fee || 0, cartCurrency.locale, cartCurrency.rate)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoConsultationRow;
